import { Baby, CalendarCheck, FileText, Home, LogOut, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "./auth";
import type { Question } from "./core";

export function LogoMark() {
  return (
    <Link to="/" className="flex items-center gap-3" aria-label="MotherSense Indonesia">
      <span className="grid size-10 place-items-center rounded-full bg-primary text-primary-foreground">
        <Baby className="size-5" />
      </span>
      <span className="leading-tight">
        <span className="block font-display text-xl text-primary">MotherSense</span>
        <span className="block text-xs uppercase tracking-[0.22em] text-muted-foreground">Indonesia</span>
      </span>
    </Link>
  );
}

export function AuthShell({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <main className="mx-auto grid min-h-[78svh] max-w-6xl items-center gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
      <div>
        <Badge>Portal MotherSense</Badge>
        <h1 className="mt-4 font-display text-4xl text-primary sm:text-5xl">{title}</h1>
        <p className="mt-4 text-muted-foreground">{description}</p>
      </div>
      <Card className="rounded-lg">
        <CardContent className="pt-6">{children}</CardContent>
      </Card>
    </main>
  );
}

export function FieldError({ message, children }: { message?: string; children: ReactNode }) {
  return (
    <div className="grid gap-2">
      {children}
      {message && <p className="text-xs text-destructive">{message}</p>}
    </div>
  );
}

export function WorkspaceHeader({ title, description }: { title: string; description: string }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="border-b bg-background">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
        <div>
          <LogoMark />
          <h1 className="mt-5 font-display text-3xl text-primary">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge>{user?.role}</Badge>
          <Button asChild variant="outline" size="sm">
            <Link to="/">
              <Home />
              Situs
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              await logout();
              navigate("/masuk");
            }}
          >
            <LogOut />
            Keluar
          </Button>
        </div>
      </div>
    </header>
  );
}

export function Metric({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: number }) {
  return (
    <div className="rounded-lg border bg-card p-5">
      <Icon className="size-5 text-primary" />
      <div className="mt-4 text-3xl font-semibold text-primary">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title }: { icon: LucideIcon; title: string }) {
  return (
    <div className="grid place-items-center rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
      <Icon className="mb-2 size-6" />
      {title}
    </div>
  );
}

export function AssessmentQuestionField({
  question,
  value,
  onChange,
}: {
  question: Question;
  value?: string;
  onChange: (next: string) => void;
}) {
  if (question.type === "textarea") {
    return <Textarea className="min-h-28" value={value ?? ""} onChange={event => onChange(event.target.value)} />;
  }

  if (question.type === "text") {
    return <Input value={value ?? ""} onChange={event => onChange(event.target.value)} />;
  }

  if (question.type === "single_choice") {
    return (
      <div className="grid gap-2">
        {(question.options ?? []).map(option => (
          <label key={option} className="flex items-center gap-3 rounded-md border p-3 text-sm">
            <input type="radio" name={question.id} value={option} checked={value === option} onChange={event => onChange(event.target.value)} />
            {option}
          </label>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-3">
      <label className={`rounded-lg border p-4 text-sm transition ${value === "a" ? "border-primary bg-secondary/60" : ""}`}>
        <div className="flex gap-3">
          <input type="radio" name={question.id} value="a" checked={value === "a"} onChange={() => onChange("a")} />
          <div>
            <div className="font-medium text-primary">A</div>
            <div className="mt-1 leading-6 text-foreground">{question.leftText}</div>
          </div>
        </div>
      </label>
      <label className={`rounded-lg border p-4 text-sm transition ${value === "b" ? "border-primary bg-secondary/60" : ""}`}>
        <div className="flex gap-3">
          <input type="radio" name={question.id} value="b" checked={value === "b"} onChange={() => onChange("b")} />
          <div>
            <div className="font-medium text-primary">B</div>
            <div className="mt-1 leading-6 text-foreground">{question.rightText}</div>
          </div>
        </div>
      </label>
      {question.parityNote && <p className="text-xs text-muted-foreground">{question.parityNote}</p>}
    </div>
  );
}
